import { useEffect, useState, useSyncExternalStore } from 'react';
import { Alert, Platform, Pressable, ScrollView, Text, View } from 'react-native';
import * as Updates from 'expo-updates';
import { C, alpha } from './theme';
import { BotaoCopiar } from './BotaoCopiar';
import { Expansivel } from '../screens/ajustes/comum';
import { t } from '../lib/i18n';
import { enviarFeedback } from '../lib/indicacao';
import { brDateTime } from '../lib/date';
import { dispensarFalhaGrave, falhaGrave, falhas, fechouSeguidas, ligarModoSeguro, limparFalhas, ouvirFalhas, registrarFalha, textoRelatorio, versaoFalhas, type Falha } from '../lib/falhas';

// Telas de proteção contra falhas (0.57). Usam só cores e textos (sem os provedores do app), porque podem aparecer
// justamente quando alguma parte do app quebrou.

const ROXO = '#8B5CF6';
const useFalhas = () => { useSyncExternalStore(ouvirFalhas, versaoFalhas); return falhas(); };

function Botao({ label, onPress, cheio, cor = ROXO }: { label: string; onPress: () => void; cheio?: boolean; cor?: string }) {
  return (
    <Pressable onPress={onPress} style={({ pressed }) => ({ paddingVertical: 13, paddingHorizontal: 18, borderRadius: 14, alignItems: 'center', opacity: pressed ? 0.75 : 1,
      backgroundColor: cheio ? cor : 'transparent', borderWidth: 1, borderColor: cor })}>
      <Text style={{ color: cheio ? '#fff' : C.text, fontWeight: '800' }}>{label}</Text>
    </Pressable>
  );
}

/** Manda o relatório de erros para a equipe (pelo servidor; sem servidor, abre o e-mail). */
async function enviarRelatorio(lista?: Falha[]) {
  try {
    const r = await enviarFeedback('erro', textoRelatorio(lista), '');
    if (r === 'ok') Alert.alert(t('Relatório enviado. Obrigado!'), t('A equipe vai olhar o erro e corrigir numa próxima atualização.'));
  } catch (e: any) { Alert.alert(t('Não foi possível'), String(e?.message ?? e)); }
}
export function reiniciarApp() {
  if (Platform.OS === 'web') { if (typeof window !== 'undefined') window.location.reload(); return; }
  Updates.reloadAsync().catch(() => Alert.alert(t('Feche e abra o app de novo'), t('Não foi possível reiniciar sozinho.')));
}

/** Uma tela quebrou: mostra isto no lugar dela, sem fechar o app (o expo-router usa o ErrorBoundary exportado de cada rota). */
export function ErrorBoundary({ error, retry }: { error: Error; retry: () => Promise<void> }) {
  useEffect(() => { registrarFalha(error, 'tela'); }, [error]);
  const [detalhes, setDetalhes] = useState(false);
  return (
    <ScrollView style={{ flex: 1, backgroundColor: C.bg }} contentContainerStyle={{ flexGrow: 1, justifyContent: 'center', padding: 24, gap: 14 }}>
      <Text style={{ fontSize: 46, textAlign: 'center' }}>🛠️</Text>
      <Text style={{ color: C.text, fontSize: 21, fontWeight: '800', textAlign: 'center' }}>{t('Esta tela teve um problema')}</Text>
      <Text style={{ color: C.sub, textAlign: 'center' }}>{t('O app não fechou e os seus dados continuam salvos. Tente de novo ou mande o relatório para a equipe corrigir.')}</Text>
      <Botao cheio label={t('Tentar de novo')} onPress={() => { retry().catch(() => {}); }} />
      <Botao label={'📤 ' + t('Enviar relatório para a equipe')} onPress={() => enviarRelatorio()} />
      <Botao label={'🔄 ' + t('Reiniciar o app')} onPress={reiniciarApp} />
      <Pressable onPress={() => setDetalhes(d => !d)}><Text style={{ color: C.sub, textAlign: 'center', fontSize: 12 }}>{detalhes ? t('Esconder detalhes') : t('Ver detalhes do erro')}</Text></Pressable>
      {detalhes && <Text selectable style={{ color: C.mute, fontSize: 11 }}>{String(error?.message ?? error)}</Text>}
    </ScrollView>
  );
}

/** Erro que antes fechava o app: agora aparece este aviso e o app continua aberto. */
export function AvisoFalha() {
  useSyncExternalStore(ouvirFalhas, versaoFalhas);
  const f = falhaGrave();
  if (!f) return null;
  return (
    <View style={{ position: 'absolute', left: 12, right: 12, bottom: 110, backgroundColor: C.card, borderRadius: 18, padding: 16, gap: 10, borderWidth: 1, borderColor: alpha(C.warn, 0.6),
      shadowColor: '#000', shadowOpacity: 0.35, shadowRadius: 14, elevation: 12 }}>
      <Text style={{ color: C.text, fontWeight: '800', fontSize: 16 }}>⚠️ {t('Algo deu errado, mas o app continua aberto')}</Text>
      <Text style={{ color: C.sub, fontSize: 13 }}>{t('Se alguma coisa não responder, reinicie o app. Os seus dados estão salvos.')}</Text>
      <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
        <Pressable onPress={() => { enviarRelatorio(); dispensarFalhaGrave(); }} style={{ paddingHorizontal: 12, paddingVertical: 8, borderRadius: 12, backgroundColor: ROXO }}>
          <Text style={{ color: '#fff', fontWeight: '700', fontSize: 13 }}>📤 {t('Enviar relatório')}</Text></Pressable>
        <Pressable onPress={reiniciarApp} style={{ paddingHorizontal: 12, paddingVertical: 8, borderRadius: 12, backgroundColor: C.card2 }}>
          <Text style={{ color: C.text, fontWeight: '700', fontSize: 13 }}>🔄 {t('Reiniciar')}</Text></Pressable>
        <Pressable onPress={dispensarFalhaGrave} style={{ paddingHorizontal: 12, paddingVertical: 8, borderRadius: 12, backgroundColor: C.card2 }}>
          <Text style={{ color: C.text, fontWeight: '700', fontSize: 13 }}>{t('Continuar')}</Text></Pressable>
      </View>
    </View>
  );
}

/** O app fechou sozinho nas últimas aberturas: pergunta se quer abrir no modo seguro. */
export function TelaModoSeguro({ onSeguir }: { onSeguir: () => void }) {
  const lista = useFalhas();
  return (
    <ScrollView style={{ flex: 1, backgroundColor: C.bg }} contentContainerStyle={{ flexGrow: 1, justifyContent: 'center', padding: 24, gap: 14 }}>
      <Text style={{ fontSize: 46, textAlign: 'center' }}>🛟</Text>
      <Text style={{ color: C.text, fontSize: 21, fontWeight: '800', textAlign: 'center' }}>{t('O app fechou sozinho {n} vezes seguidas', { n: fechouSeguidas() })}</Text>
      <Text style={{ color: C.sub, textAlign: 'center' }}>{t('No modo seguro o app abre sem as tarefas automáticas (avisos, agenda do Google, publicações, mensagens e relógio). Os seus dados não mudam e tudo volta ao normal na próxima vez que abrir.')}</Text>
      <Botao cheio label={'🛟 ' + t('Abrir no modo seguro')} onPress={() => { ligarModoSeguro(true); onSeguir(); }} />
      <Botao label={t('Abrir normalmente')} onPress={onSeguir} />
      <Botao label={'📤 ' + t('Enviar relatório para a equipe')} onPress={() => enviarRelatorio(lista)} />
      {!!lista.length && <Text style={{ color: C.mute, fontSize: 11, textAlign: 'center' }}>{t('Último erro: {m}', { m: lista[0].msg.slice(0, 160) })}</Text>}
    </ScrollView>
  );
}

/** Ajustes > Fale conosco: erros guardados neste aparelho.
 *  0.67.2 (texto 07 C8): o cartão abre e fecha (▸/▾, a seção recolhível única do app). Aberto: sem erros, "Nenhum erro registrado";
 *  com erros, a lista, e cada erro com "Copiar" e "Enviar para a equipe" (os botões de enviar tudo e apagar a lista continuam). */
export function RelatorioFalhas({ cor }: { cor: string }) {
  const lista = useFalhas();
  return (
    <View style={{ backgroundColor: C.card, borderRadius: 18, paddingHorizontal: 14, paddingVertical: 6 }}>
      <Expansivel titulo={'🐞 ' + t('Erros registrados neste dispositivo ({n})', { n: lista.length })} estiloTitulo={{ fontWeight: '800' }} gap={10}>
        {!lista.length && <Text style={{ color: C.sub, fontSize: 13, paddingBottom: 8 }}>{t('Nenhum erro registrado. Se o app fechar sozinho ou uma tela der problema, o erro aparece aqui para você mandar para a equipe.')}</Text>}
        {!!lista.length && (<>
          <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
            <Pressable onPress={() => enviarRelatorio(lista)} style={{ paddingHorizontal: 12, paddingVertical: 8, borderRadius: 12, backgroundColor: cor }}>
              <Text style={{ color: '#fff', fontWeight: '700', fontSize: 13 }}>📤 {t('Enviar todos para a equipe')}</Text></Pressable>
            <Pressable onPress={() => Alert.alert(t('Apagar a lista de erros?'), '', [{ text: t('Cancelar'), style: 'cancel' }, { text: t('Apagar'), style: 'destructive', onPress: limparFalhas }])}
              style={{ paddingHorizontal: 12, paddingVertical: 8, borderRadius: 12, backgroundColor: C.card2 }}>
              <Text style={{ color: C.text, fontWeight: '700', fontSize: 13 }}>{t('Apagar lista')}</Text></Pressable>
          </View>
          {lista.map((f, i) => (
            <View key={i} style={{ borderTopWidth: 1, borderTopColor: C.line, paddingTop: 8, paddingBottom: i === lista.length - 1 ? 8 : 0, gap: 4 }}>
              <Text style={{ color: f.fatal ? C.warn : C.sub, fontSize: 12, fontWeight: '700' }}>{brDateTime(f.quando)} · {f.onde}</Text>
              <Text selectable style={{ color: C.text, fontSize: 12 }}>{f.msg}</Text>
              <Text style={{ color: C.mute, fontSize: 11 }}>{f.versao} · {f.aparelho}</Text>
              <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <BotaoCopiar cor={cor} texto={textoRelatorio([f])} rotulo={t('Copiar')} />
                <Pressable onPress={() => enviarRelatorio([f])} style={{ paddingHorizontal: 10, paddingVertical: 6, borderRadius: 12, borderWidth: 1, borderColor: cor }}>
                  <Text style={{ color: cor, fontWeight: '700', fontSize: 12 }}>📤 {t('Enviar para a equipe')}</Text></Pressable>
              </View>
            </View>
          ))}
        </>)}
      </Expansivel>
    </View>
  );
}
